/**
 * Aba Contratos: chips de status (Ativos|Expirados|Cancelados|Todos), CTA
 * '+ Contrato', gear 'Tipos de contrato' e tabela compacta com ações no hover.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Ban, Pencil, Plus, Settings } from 'lucide-react';
import { MODALITY_LABELS } from '@erp/shared';
import type { Contract, ContractStatus } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import {
  contractsOptions,
  errorMessage,
  type ContractStatusFilter,
} from './billing-queries';
import { useCancelContract } from './billing-mutations';
import { formatBRL, formatVigencia } from './billing-format';
import { ContractDrawer } from './contract-drawer';
import { ContractTypesDialog } from './contract-types-dialog';

const STATUS_FILTERS: { value: ContractStatusFilter; label: string }[] = [
  { value: 'active', label: 'Ativos' },
  { value: 'expired', label: 'Expirados' },
  { value: 'canceled', label: 'Cancelados' },
  { value: 'all', label: 'Todos' },
];

export const CONTRACT_STATUS_CHIP: Record<
  ContractStatus,
  { label: string; variant: 'success' | 'warning' | 'destructive' }
> = {
  active: { label: 'Ativo', variant: 'success' },
  expired: { label: 'Expirado', variant: 'warning' },
  canceled: { label: 'Cancelado', variant: 'destructive' },
};

type DrawerState = { mode: 'new' } | { mode: 'edit'; contract: Contract };

export function ContractsTab() {
  const [status, setStatus] = useState<ContractStatusFilter>('active');
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [typesOpen, setTypesOpen] = useState(false);
  const [toCancel, setToCancel] = useState<Contract | null>(null);

  const contractsQ = useQuery(contractsOptions({ status }));
  const cancel = useCancelContract();

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-4 pb-3">
        {STATUS_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            aria-pressed={status === value}
            onClick={() => setStatus(value)}
            className={cn(
              'inline-flex h-8 items-center rounded-md border px-3 text-[13px] font-medium transition-colors',
              status === value
                ? 'border-transparent bg-foreground text-background'
                : 'border-border bg-card text-foreground hover:bg-muted/60'
            )}
          >
            {label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setTypesOpen(true)}>
            <Settings />
            Tipos de contrato
          </Button>
          <Button size="sm" onClick={() => setDrawer({ mode: 'new' })}>
            <Plus />
            Contrato
          </Button>
        </div>
      </div>

      {/* Tabela */}
      {contractsQ.isPending ? (
        <div aria-busy className="space-y-3 border-t border-divider p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-4 w-1/5" />
              <Skeleton className="h-4 w-1/6" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-16 rounded-sm" />
            </div>
          ))}
        </div>
      ) : contractsQ.isError ? (
        <div className="border-t border-divider">
          <EmptyState message={`Erro ao carregar contratos: ${errorMessage(contractsQ.error)}`} />
        </div>
      ) : contractsQ.data.length === 0 ? (
        <div className="border-t border-divider">
          <EmptyState message="Nenhum contrato encontrado" />
        </div>
      ) : (
        <div className="overflow-x-auto border-t border-divider">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Nome</th>
                <th className="h-9 px-4 font-semibold">Cliente</th>
                <th className="h-9 px-4 font-semibold">Tipo</th>
                <th className="h-9 px-4 font-semibold">Valor</th>
                <th className="h-9 px-4 font-semibold">Vigência</th>
                <th className="h-9 px-4 font-semibold">Status</th>
                <th className="h-9 px-4 text-right font-semibold">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {contractsQ.data.map((c) => {
                const chip = CONTRACT_STATUS_CHIP[c.status];
                return (
                  <tr
                    key={c.id}
                    className="group h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-56 truncate px-4 font-medium" title={c.name}>
                      {c.name}
                    </td>
                    <td className="max-w-48 truncate px-4" title={c.clientName}>
                      {c.clientName}
                    </td>
                    <td className="px-4 whitespace-nowrap">
                      <span className="mr-2 text-muted-foreground">{c.contractTypeName}</span>
                      <Badge variant="primary">{MODALITY_LABELS[c.modality]}</Badge>
                    </td>
                    <td className="px-4 whitespace-nowrap tabular-nums">{formatBRL(c.value)}</td>
                    <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                      {formatVigencia(c.startDate, c.expirationDate)}
                    </td>
                    <td className="px-4 whitespace-nowrap">
                      <Badge variant={chip.variant}>{chip.label}</Badge>
                    </td>
                    <td className="px-4">
                      <div className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          title="Editar contrato"
                          aria-label={`Editar contrato ${c.name}`}
                          onClick={() => setDrawer({ mode: 'edit', contract: c })}
                        >
                          <Pencil />
                        </Button>
                        {c.status === 'active' ? (
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            title="Cancelar contrato"
                            aria-label={`Cancelar contrato ${c.name}`}
                            className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                            onClick={() => setToCancel(c)}
                          >
                            <Ban />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {drawer ? (
        <ContractDrawer
          initial={drawer.mode === 'edit' ? drawer.contract : undefined}
          onClose={() => setDrawer(null)}
        />
      ) : null}
      {typesOpen ? <ContractTypesDialog onClose={() => setTypesOpen(false)} /> : null}

      {toCancel ? (
        <Dialog open onClose={() => setToCancel(null)} title="Cancelar contrato">
          <p className="text-sm text-muted-foreground">
            Cancelar o contrato <span className="font-medium text-foreground">{toCancel.name}</span>{' '}
            de {toCancel.clientName}? Ele deixará de gerar faturamentos pendentes.
          </p>
          {cancel.error ? (
            <p className="mt-2 text-sm text-destructive">{errorMessage(cancel.error)}</p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setToCancel(null)}>
              Voltar
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={cancel.isPending}
              onClick={() =>
                cancel.mutate(toCancel.id, { onSuccess: () => setToCancel(null) })
              }
            >
              {cancel.isPending ? 'Cancelando…' : 'Cancelar contrato'}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
